<template>
  <AdminLayout>
    <template #breadcrumb>
      <breadcrumb-link view="AdminPayerList" label="Payers" :active="true" />
    </template>

    <div class="box" style="position: relative">
      <b-loading :is-full-page="false" v-model="loading"></b-loading>
      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <h1 class="title">Manage Payers</h1>
          </div>
        </div>
        <div class="level-right">
          <div class="level-item">
            <b-button
              type="is-primary is-outlined"
              tag="router-link"
              icon-left="account-group"
              :to="{ name: 'AdminPayerCreate' }"
            >
              Create Payer
            </b-button>
          </div>
          <div class="level-item">
            <b-button
              type="is-primary is-outlined"
              tag="router-link"
              icon-left="cloud-upload"
              :to="{ name: 'AdminPayerUpload' }"
            >
              Upload Payers
            </b-button>
          </div>
          <div class="level-item">
            <b-dropdown aria-role="menu" position="is-bottom-left">
              <template #trigger>
                <b-button type="is-primary is-outlined" icon-left="sync">
                  Sync Payers
                  <b-icon icon="menu-down"></b-icon>
                </b-button>
              </template>
              <b-dropdown-item aria-role="menu-item" @click="handleSyncAll('all')">
                <div class="is-flex">
                  <span>All Services</span>
                </div>
              </b-dropdown-item>
              <b-dropdown-item aria-role="menu-item" @click="handleSyncAll('a')">
                <div class="is-flex">
                  <b-icon icon="alpha-a-circle" class="mr-1" type="is-info" />
                  <span>A Only</span>
                </div>
              </b-dropdown-item>
              <b-dropdown-item aria-role="menu-item" @click="handleSyncAll('p')">
                <div class="is-flex">
                  <b-icon icon="alpha-p-circle" class="mr-1" type="is-primary" />
                  <span>P Only</span>
                </div>
              </b-dropdown-item>
            </b-dropdown>
          </div>
          <div class="level-item">
            <b-dropdown aria-role="menu" position="is-bottom-left">
              <template #trigger>
                <b-button type="is-primary is-outlined" icon-left="database-sync">
                  Sync Schemas
                  <b-icon icon="menu-down"></b-icon>
                </b-button>
              </template>
              <b-dropdown-item aria-role="menu-item" @click="handleSchemaSyncAll('a')">
                <div class="is-flex">
                  <b-icon icon="alpha-a-circle" class="mr-1" type="is-info" />
                  <span>A Only</span>
                </div>
              </b-dropdown-item>
              <b-dropdown-item aria-role="menu-item" @click="handleSchemaSyncAll('p')">
                <div class="is-flex">
                  <b-icon icon="alpha-p-circle" class="mr-1" type="is-primary" />
                  <span>P Only</span>
                </div>
              </b-dropdown-item>
            </b-dropdown>
          </div>
          <div class="level-item">
            <b-button
              type="is-primary is-outlined"
              tag="router-link"
              icon-left="call-merge"
              :to="{ name: 'AdminPayerMerge' }"
            >
              Merge Payers
            </b-button>
          </div>
        </div>
      </div>
      <hr />

      <div class="level">
        <div class="level-left">
          <div class="level-item">
            <b-field grouped label="Payer Group" v-if="payerGroupOptions">
              <b-select
                v-model="payerGroupSelected"
                placeholder="Change group for selected"
                :loading="loading"
                :disabled="!checkedRows.length || !payerGroupOptions.length"
                @input="handlePayerGroupBulkAdd($event)"
              >
                <option v-for="option in payerGroupOptions" :value="option.id" :key="option.id">
                  {{ option.name }}
                </option>
              </b-select>
            </b-field>
          </div>
        </div>
      </div>

      <!-- Search Section -->
      <b-field>
        <div class="field-body">
          <b-input
            class="is-expanded"
            :loading="loading"
            v-model="search"
            placeholder="Search by Name, A Name, P Name, Code, or P Code"
            @input="emitSearchTerm"
          />
          <b-dropdown
            class="filter-dropdown"
            position="is-bottom-left"
            v-model="searchableFields"
            multiple
            aria-role="list"
          >
            <template #trigger>
              <b-button icon-right="filter" type="is-primary">
                Search Fields ({{ searchableFields.length }})
              </b-button>
            </template>

            <b-dropdown-item
              v-for="field in allFields"
              :key="field.value"
              :value="field.value"
              aria-role="listitem"
            >
              <div @click="toggleCheckbox(field.value)">
                <b-checkbox
                  v-model="searchableFields"
                  :native-value="field.value"
                  class="checkbox-left"
                >
                  {{ field.label }}
                </b-checkbox>
              </div>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </b-field>

      <b-table
        v-if="payerOptions"
        :data="payerOptions"
        sort-icon="chevron-up"
        sort-icon-size="is-small"
        backend-sorting
        :default-sort="[sortField, sortOrder]"
        @sort="handleSort"
        hoverable
        striped
        :checked-rows.sync="checkedRows"
        checkable
        detailed
        detail-key="id"
      >
        <b-table-column field="name" label="Name" sortable v-slot="props">
          <router-link :to="{ name: 'AdminPayerDetail', params: { id: props.row.id } }">
            {{ props.row.name }}
          </router-link>
        </b-table-column>

        <b-table-column field="payer_group.id" sortable label="Group" v-slot="props">
          <span v-if="props.row.is_group">
            <b-icon type="is-success" icon="check-circle" size="is-small" />
          </span>
          <b-tag
            v-else-if="props.row.payer_group"
            type="is-info"
            closable
            aria-close-label="Remove group"
            @close="handlePayerGroupRemove(props.row.id)"
          >
            {{ props.row.payer_group.name }}
          </b-tag>
        </b-table-column>

        <b-table-column field="is_blue" label="Blue" sortable v-slot="props">
          <b-switch
            type="is-info"
            @input="handleIsBlueSwitch($event, props.row.id)"
            :value="props.row.is_blue"
            :disabled="indexClicked === props.row.id"
          />
        </b-table-column>
        <b-table-column field="is_active" label="A Active" sortable v-slot="props">
          <b-switch
            type="is-success"
            v-model="props.row.is_active"
            :disabled="indexClicked === props.row.id || !props.row.is_available || !props.row.code"
            @input="handleIsActiveSwitch($event, props.row.id)"
          />
        </b-table-column>
        <b-table-column field="p_is_active" label="P Active" sortable v-slot="props">
          <b-switch
            type="is-success"
            v-model="props.row.p_is_active"
            :disabled="
              indexClicked === props.row.id || !props.row.p_is_available || !props.row.p_code
            "
            @input="handlePIsActiveSwitch($event, props.row.id)"
          />
        </b-table-column>
        <b-table-column field="source" label="Source" v-slot="props">
          <div class="is-flex">
            <b-tooltip
              v-if="props.row.code && props.row.is_available"
              label="A: Available"
              position="is-left"
              type="is-info"
            >
              <b-icon icon="alpha-a-circle" class="mr-1" type="is-info" />
            </b-tooltip>
            <b-tooltip
              v-if="props.row.code && !props.row.is_available"
              label="A: Unavailable"
              position="is-left"
              type="is-danger"
            >
              <b-icon icon="alpha-a-circle" class="mr-1" type="is-danger" />
            </b-tooltip>
            <b-tooltip
              v-if="props.row.p_code && props.row.p_is_available"
              label="P: Available"
              position="is-left"
              type="is-primary"
            >
              <b-icon icon="alpha-p-circle" class="mr-1" type="is-primary" />
            </b-tooltip>
            <b-tooltip
              v-if="props.row.p_code && !props.row.p_is_available"
              label="P: Unavailable"
              position="is-left"
              type="is-danger"
            >
              <b-icon icon="alpha-p-circle" class="mr-1" type="is-danger" />
            </b-tooltip>
          </div>
        </b-table-column>
        <b-table-column field="created_at" label="Created" sortable v-slot="props">
          {{ formatToPST(props.row.created_at) }}
        </b-table-column>
        <b-table-column label="Actions" v-slot="props">
          <div class="is-flex">
            <div style="text-align: left">
              <b-tooltip label="Sync schema">
                <b-button
                  @click="handleSchemaSync(props.row.id)"
                  type="is-info is-light"
                  icon-left="database-sync"
                  :loading="indexSchemaSynced.includes(props.row.id)"
                />
              </b-tooltip>
            </div>
            <div style="text-align: right">
              <b-tooltip label="Sync payer">
                <b-button
                  @click="handleSync(props.row.id)"
                  type="is-info is-light"
                  icon-left="sync"
                  :loading="indexSynced.includes(props.row.id)"
                />
              </b-tooltip>
            </div>
          </div>
        </b-table-column>

        <template #detail="props">
          <div class="content">
            <p>
              <strong>ID:</strong>
              {{ props.row.id }}
              <br />
              <strong>Name:</strong>
              {{ props.row.name }}
              <br />
              <strong>A Name :</strong>
              {{ props.row.name_internal }}
              <br />
              <strong>A Code:</strong>
              {{ props.row.code }}
              <br />
              <strong>A Is Available:</strong>
              {{ props.row.is_available ? 'Yes' : 'No' }}
              <br />
              <strong>A Is Active:</strong>
              {{ props.row.is_active ? 'Yes' : 'No' }}
              <br />
              <strong>P Name:</strong>
              {{ props.row.p_name }}
              <br />
              <strong>P Code:</strong>
              {{ props.row.p_code }}
              <br />
              <strong>P Is Active:</strong>
              {{ props.row.p_is_active ? 'Yes' : 'No' }}
              <br />
              <strong>P Is Available:</strong>
              {{ props.row.p_is_available ? 'Yes' : 'No' }}
              <br />
              <strong>Blue:</strong>
              {{ props.row.is_blue ? 'Yes' : 'No' }}
              <br />
              <strong>Is Group:</strong>
              {{ props.row.is_group ? 'Yes' : 'No' }}
              <br />
              <strong>Split Service Type:</strong>
              {{ props.row.split_service_type ? 'Yes' : 'No' }}
              <br />
              <strong>Payer Group ID:</strong>
              {{ props.row.payer_group_id }}
              <br />
              <strong>Created At:</strong>
              {{ formatToPST(props.row.created_at) }} PST
              <br />
              <strong>Updated At:</strong>
              {{ formatToPST(props.row.updated_at) }} PST
              <br />
            </p>
          </div>
        </template>
      </b-table>
      <div class="pagination-container" v-if="totalItems && pageSize">
      <b-pagination
        v-model="currentPage"
        :total="totalItems"
        :per-page="pageSize"
        @change="handlePageChange"
      ></b-pagination>
    </div>
    </div>
  </AdminLayout>
</template>

<script>
import axios from 'axios';
import request from '../../request';
import AdminLayout from '../../layouts/AdminLayout.vue';

export default {
  name: 'AdminPayerList',
  components: {
    AdminLayout,
  },
  data() {
    return {
      loading: true,
      payerOptions: [],
      payerGroupOptions: [],
      checkedRows: [],
      currentPage: 1,
      pageSize: 15,
      totalItems: 0,
      sortField: 'created_at',
      sortOrder: 'desc',
      indexSynced: [],
      indexClicked: null,
      payerGroupSelected: null,
      indexSchemaSynced: [],

      // Search functionality
      search: '',
      searchableFields: ['name', 'name_internal', 'p_name'],
      allFields: [
        { value: 'name', label: 'Name' },
        { value: 'p_name', label: 'P Name' },
        { value: 'name_internal', label: 'A Name' },
        { value: 'code', label: 'A Code' },
        { value: 'p_code', label: 'P Code' },
      ],
    };
  },
  created() {
    this.loading = true;
    this.fetchPayers();
  },
  methods: {
    fetchPayers() {
      let args = {
        page: this.currentPage,
        limit: this.pageSize,
        sort_field: this.sortField,
        sort_order: this.sortOrder,
      };

      // Add search parameters if they exist. The same term is sent for each
      // selected field; the backend OR-matches them.
      if (this.search && this.searchableFields && this.searchableFields.length > 0) {
        this.searchableFields.forEach((field) => {
          args[field] = this.search;
        });
      }

      let params = new URLSearchParams(args).toString();
      let url = '/admin/payers';
      if (params) {
        url += `?${params}`;
      }

      return request
        .get(url)
        .then((resp) => {
          this.payerOptions = resp.data.payers;
          this.totalItems = Number(resp.headers['x-total-count']);
          this.payerGroupOptions = resp.data.payers.filter((payer) => payer.is_group);
        })
        .finally(() => (this.loading = false));
    },
    emitSearchTerm() {
      this.fetchPayers();
    },
    toggleCheckbox(value) {
      const index = this.searchableFields.indexOf(value);
      if (index === -1) {
        this.searchableFields.push(value); // Add the field to the selected list
      } else {
        this.searchableFields.splice(index, 1); // Remove the field from the selected list
      }
      this.emitSearchTerm();
    },
    handleSync(id) {
      this.indexSynced.push(id);
      request
        .post(`/admin/payers/${id}/sync`)
        .then((resp) => {
          this.$buefy.toast.open({
            message: resp.data.message,
            type: 'is-success',
            duration: 9000, // Increased duration to read the longer message
          });
        })
        .catch((error) => {
          if (error.response.status == 502) {
            let msg = "A doesn't have data on this payer.";
            this.$buefy.toast.open({ message: msg, type: 'is-danger' });
          } else {
            let msg = 'There was an unknown error.';
            this.$buefy.toast.open({ message: msg, type: 'is-danger' });
          }
        })
        .finally(() => {
          this.indexSynced = this.indexSynced.filter(function (item) {
            return item !== id;
          });
        });
    },
    handleSyncAll(service = 'all') {
      this.loading = true;
      request
        .post(`/admin/payers/sync?service=${service}`)
        .then((resp) => {
          this.fetchPayers();

          const stats = resp.data;
          let serviceText =
            service === 'all' ? 'All services' : service === 'a' ? 'A only' : 'P only';

          let msg = `Sync completed for ${serviceText}\n`;

          if (service === 'all' || service === 'a') {
            msg += `<br/>A: ${stats.a_new_payers} new, ${stats.a_updated_payers} updated`;
          }

          if (service === 'all' || service === 'p') {
            msg += `<br/>P: ${stats.p_new_payers} new, ${stats.p_updated_payers} updated`;
          }

          msg += `<br/>Schema is processing...`;

          this.$buefy.toast.open({
            message: msg,
            type: 'is-success',
            duration: 9000, // Increased duration to read the longer message
          });
        })
        .catch(() => {
          let msg = 'There was an unknown error.';
          this.$buefy.toast.open({ message: msg, type: 'is-danger' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handleIsActiveSwitch(isActive, id) {
      this.indexClicked = id;
      request
        .put(`/admin/payers/${id}`, { is_active: isActive })
        .finally(() => (this.indexClicked = null));
    },
    handlePIsActiveSwitch(isActive, id) {
      this.indexClicked = id;
      request
        .put(`/admin/payers/${id}`, { p_is_active: isActive })
        .finally(() => (this.indexClicked = null));
    },
    handleIsBlueSwitch(isBlue, id) {
      this.indexClicked = id;
      request
        .put(`/admin/payers/${id}`, { is_blue: isBlue })
        .finally(() => (this.indexClicked = null));
    },
    handlePayerGroupBulkAdd(payerGroupId) {
      let payerGroup = this.payerOptions.find((opt) => opt.id === payerGroupId);

      let message = `Are you sure you want to add ${this.checkedRows.length} payers to "${payerGroup.name}"?`;
      let is = payerGroup.is_blue ? 'IS' : 'IS NOT';
      message += `<br/><br/><strong class="has-text-danger">NOTE: "${payerGroup.name}" ${is} a BLUE. All these payers will be set to the same.</strong>`;

      this.$buefy.dialog.confirm({
        message: message,
        confirmText: `Add to ${payerGroup.name}`,
        type: 'is-success',
        onConfirm: () => {
          this.loading = true;
          let ids = this.checkedRows.map((r) => r.id);
          request
            .put(`/admin/payers/${payerGroupId}/bulk`, { payer_ids: ids })
            .then(() => {
              this.fetchPayers();
            })
            .finally(() => {
              this.checkedRows = [];
              this.payerGroupSelected = null;
              this.loading = false;
            });
        },
      });
    },
    handlePayerGroupRemove(payerId) {
      let payer = this.payerOptions.find((opt) => opt.id === payerId);
      this.$buefy.dialog.confirm({
        message: `Are you sure you want to remove "${payer.name}" from the group "${payer.payer_group.name}"?`,
        confirmText: 'Remove From Group',
        type: 'is-danger',
        onConfirm: () => {
          this.loading = true;
          request
            .put(`/admin/payers/${payerId}`, { payer_group_id: 0 })
            .then(() => {
              axios.all([
                request.post(`/admin/payers/${payer.payer_group.id}/schema`),
                this.fetchPayers(),
              ]);
            })
            .finally(() => (this.loading = false));
        },
      });
    },
    handleSchemaSync(id) {
      this.indexSchemaSynced.push(id);
      request
        .post(`/admin/payers/${id}/schema`)
        .then(() => {
          this.$buefy.toast.open({
            message: 'Schema sync completed',
            type: 'is-success',
          });
        })
        .catch((error) => {
          if (error.response.status == 502) {
            let msg = 'Failed to sync schema for this payer.';
            this.$buefy.toast.open({ message: msg, type: 'is-danger' });
          } else {
            let msg = 'There was an unknown error.';
            this.$buefy.toast.open({ message: msg, type: 'is-danger' });
          }
        })
        .finally(() => {
          this.indexSchemaSynced = this.indexSchemaSynced.filter((item) => item !== id);
        });
    },
    handleSchemaSyncAll(service) {
      this.loading = true;
      request
        .post(`/admin/payers/schemas?service=${service}`)
        .then(() => {
          let serviceText = service === 'a' ? 'A' : 'P';
          this.$buefy.toast.open({
            message: `${serviceText} schemas sync completed`,
            type: 'is-success',
            duration: 5000,
          });
        })
        .catch(() => {
          let serviceText = service === 'a' ? 'A' : 'P';
          let msg = `There was an error syncing ${serviceText} schemas.`;
          this.$buefy.toast.open({ message: msg, type: 'is-danger' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
    handlePageChange(newPage) {
      this.currentPage = newPage;
      this.fetchPayers();
    },
    handleSort(field, order) {
      this.sortField = field;
      this.sortOrder = order;
      this.currentPage = 1;
      this.fetchPayers();
    },
  },
};
</script>

<script setup>
const formatToPST = (timestamp) => {
  return new Date(timestamp).toLocaleString('en-US', {
    timeZone: 'America/Los_Angeles',
    dateStyle: 'medium',
  });
};
</script>

<style scoped>
.field-body {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.is-expanded {
  flex-grow: 1;
}
.filter-dropdown {
  margin-left: 10px;
}
/* Prevent the background color on selection */
.filter-dropdown .dropdown-item {
  color: black !important;
}

/*Remove background color on hover or active state */
.filter-dropdown .dropdown-item.is-active,
.filter-dropdown .dropdown-item.is-hovered {
  background-color: transparent !important;
}
.checkbox-left {
  display: inline-block;
  padding-left: 10px;
  margin-right: 10px;
}
.filter-dropdown .dropdown-item .b-checkbox {
  display: inline-flex;
  align-items: center;
}
.pagination-container {
  display: flex;
  justify-content: flex-end;
}
</style>
